import { Timestamp } from 'firebase/firestore';

import { parseDeltaComment } from './reviewCommentParsing';
import { extractReviewPhotoFields } from './reviewPhotoFields';
import { parseReactionSummary } from './reviewReactionTypes';

/**
 * Normalizes a Firestore review date field to a Date instance.
 * Returns the current date/time when dateValue is missing or falsy.
 * @param {import('firebase/firestore').Timestamp | Date | string | number | undefined} dateValue
 * @returns {Date}
 */
export function normalizeReviewDate(dateValue) {
  if (dateValue instanceof Timestamp) {
    return dateValue.toDate();
  }
  if (dateValue) {
    return new Date(dateValue);
  }
    return new Date();
}

/**
 * Optional editedAt; omitted when the field is missing.
 * @param {import('firebase/firestore').Timestamp | Date | string | number | undefined} dateValue
 * @returns {Date | undefined}
 */
export function normalizeOptionalReviewDate(dateValue) {
  if (dateValue == null || dateValue === '') {
    return undefined;
  }
  return normalizeReviewDate(dateValue);
}

/**
 * Maps a beer review Firestore document plus denormalized names to BeerReviewCard shape.
 * @param {object} params
 * @param {string} params.reviewId
 * @param {object} params.reviewData
 * @param {string} [params.beerName]
 * @param {string} [params.breweryName]
 * @param {string} [params.userName]
 * @param {string} [params.consumptionPlaceName]
 * @returns {object}
 */
export function mapBeerReviewDoc({
  reviewId,
  reviewData,
  beerName = 'Unknown Beer',
  breweryName = 'Unknown Brewery',
  userName = 'Unknown User',
  consumptionPlaceName,
}) {
  const safeReviewData =
    reviewData && typeof reviewData === 'object' ? reviewData : {};
  const safeReviewId = typeof reviewId === 'string' ? reviewId : '';
  const beerId = safeReviewData.beerId;
  const breweryId = safeReviewData.breweryId;
  const userId = safeReviewData.uid;
  const rawConsumptionType = safeReviewData.consumptionPlaceType;
  const consumptionPlaceType = typeof rawConsumptionType === 'string'
    && rawConsumptionType.trim().length > 0
    ? rawConsumptionType.trim()
    : undefined;
  const rawConsumptionId = safeReviewData.consumptionPlaceId;
  const consumptionPlaceId = typeof rawConsumptionId === 'string'
    && rawConsumptionId.trim().length > 0
    ? rawConsumptionId.trim()
    : undefined;

  const rawStoredName = safeReviewData.consumptionPlaceName;
  const hydratedName = typeof consumptionPlaceName === 'string'
    && consumptionPlaceName.trim().length > 0
    ? consumptionPlaceName.trim()
    : undefined;
  const storedName = typeof rawStoredName === 'string'
    && rawStoredName.trim().length > 0
    ? rawStoredName.trim()
    : undefined;
  const photoFields = extractReviewPhotoFields(safeReviewData);

  return {
    id: safeReviewId,
    beerId,
    beerName,
    breweryId,
    breweryName,
    userId,
    userName,
    date: normalizeReviewDate(safeReviewData.date),
    comment: parseDeltaComment(safeReviewData.comment),
    ratings: safeReviewData.ratings || {},
    tags: safeReviewData.tags && typeof safeReviewData.tags === 'object'
      ? safeReviewData.tags
      : {},
    consumptionPlaceType,
    consumptionPlaceId,
    consumptionPlaceName: hydratedName ?? storedName,
    reactionSummary: parseReactionSummary(safeReviewData.reactionSummary),
    editedAt: normalizeOptionalReviewDate(safeReviewData.editedAt),
    experienceDate: normalizeOptionalReviewDate(safeReviewData.experienceDate),
    photoIds: photoFields.photoIds,
    photoRevision: photoFields.photoRevision,
    photoSummary: photoFields.photoSummary,
  };
}
